import {
  DEFAULT_TIMEZONE,
  endMinute,
  isOnline,
  localDate,
  startMinute,
  zonedIso,
  type Opportunity,
} from '../../shared/opportunity';
import type { SpanOf } from './conflicts';
import type { Home } from './dayRoute';
import { areaForCoords, estimateTravel, travelOptions, type TravelEstimate, type TravelMode } from './travel';

/**
 * A journey between two events, as two separate spans.
 *
 *   window   — the time you have. From the end of whatever ran last to the
 *              start of the next thing, whether or not that is enough.
 *   required — the time the journey actually takes. Independent of the window,
 *              and drawn at its true length even when it does not fit, because
 *              a block squeezed to fit the gap would understate exactly the
 *              problem it exists to show.
 *
 * The two used to be one thing, which made "you have 20 minutes and it takes
 * 45" impossible to draw honestly.
 *
 * **Online events leave you where you were.** A virtual session in the middle
 * of the afternoon is not a place you travel to, so it produces no leg of its
 * own; the next journey is measured from the last venue you physically stood
 * in. Its window still starts when the online session ends, though — you were
 * busy until then. So in-person A, online B, in-person C gives one leg, from
 * A's location to C's, departing no earlier than B's end.
 */

/** Legs are dragged in quarter-hours. Finer would be false precision. */
export const DRAG_STEP_MINUTES = 15;

/**
 * Only gaps under two hours get a leg. Past that, travel is not the constraint
 * — you have all afternoon — and a two-hour dashed box says nothing while
 * taking up most of the column.
 */
export const MAX_WINDOW_MINUTES = 120;

export interface TravelLeg {
  /** Stable across reloads: what a saved position and mode are keyed on. */
  id: string;
  /** The last event you were physically at. Not necessarily the previous one. */
  from: Opportunity;
  to: Opportunity;
  /** Whatever ran immediately before `to`, and ends at `windowStart`: `from`, unless an online session came between. */
  previous: Opportunity;
  /** End of whatever ran immediately before `to` — online sessions included. */
  windowStart: number;
  /** Start of `to`. */
  windowEnd: number;
  estimate: TravelEstimate;
  /** Where the block sits now, in minutes from midnight. */
  startMinutes: number;
  /** True when the journey cannot fit the window however it is placed. */
  tight: boolean;
  /** Minutes short. Zero when it fits. */
  shortBy: number;
  /** True when an online session sits between the two venues. */
  viaOnline: boolean;
}

/** What a user chose for one leg, and what is written to localStorage. */
export interface LegChoice {
  mode?: TravelMode;
  /** Departure time. Absent means "wherever the default puts it". */
  startMinutes?: number;
}

export type LegChoices = Record<string, LegChoice>;

const publishedSpan: SpanOf = (e) => ({
  start: startMinute(e) ?? 0,
  end: endMinute(e) ?? (startMinute(e) ?? 0) + 60,
});

export function legId(dayKey: string, from: Opportunity, to: Opportunity): string {
  return `${dayKey}:${from.id}>${to.id}`;
}

/** The hotel's id, at either end of a journey. */
export const HOME_ID = 'home';
/** What a hotel leg's block calls that end. */
const HOME_WORD = 'Hotel';

export const isHome = (e: Opportunity) => e.id === HOME_ID;

/**
 * Where the user is staying, as a place a journey can start or end at. Not an
 * event: it has no hours of its own, and stands at the start of the day only
 * so that anything asking which day it is on gets the right answer.
 */
export function homePlace(home: Home, dayKey: string): Opportunity {
  const at = zonedIso(dayKey, 0, DEFAULT_TIMEZONE);
  return {
    id: HOME_ID,
    sourceProvider: 'personal',
    sourceUrl: '',
    registrationUrl: null,
    organizationId: null,
    organizationName: '',
    name: home.label,
    summary: null,
    description: null,
    startsAt: at,
    endsAt: at,
    isAllDay: false,
    timezone: DEFAULT_TIMEZONE,
    format: 'in_person',
    eventType: null,
    rsvpType: null,
    category: null,
    topics: [],
    goals: [],
    neighborhood: areaForCoords(home.coords),
    locationType: 'address',
    address: home.label,
    latitude: home.coords[0],
    longitude: home.coords[1],
    isOutdoor: null,
    isOfficial: false,
    isPaid: null,
    priceAmount: null,
    priceCurrency: null,
    createdAt: '',
    updatedAt: '',
  };
}

/**
 * Where a leg sits when nobody has dragged it: as late as it can go without
 * departing before you are free.
 *
 * One rule covers both cases in the reference. When the journey fits, the late
 * limit is the next event's start, so the block ends exactly as that event
 * begins — you arrive just in time. When it does not fit, that would mean
 * leaving before the previous event ends, so it clamps to the window's start
 * instead and overhangs into the next event. Which is the honest picture: you
 * leave the moment you are free and you are still late.
 */
export function defaultStart(windowStart: number, windowEnd: number, required: number): number {
  return travelBounds(windowStart, windowEnd, required).latest;
}

/**
 * The two ends of a block's travel, whichever way round they fall.
 *
 *   windowStart          — leave the moment the last event ends.
 *   windowEnd - required — arrive exactly as the next event starts.
 *
 * When the journey fits, the first is the earlier of the two and the block
 * moves between them without touching either event. When it does not fit they
 * swap over, and every position in between overhangs one event or the other:
 * leave on time and arrive late, or arrive on time and leave early. Both are
 * real choices a person makes, so both ends have to be reachable.
 *
 * The floor used to be `windowStart` outright, which silently removed half that
 * range — a journey too long for its gap could only ever overhang forwards.
 */
export function travelBounds(
  windowStart: number,
  windowEnd: number,
  required: number,
): { earliest: number; latest: number } {
  const onTimeDeparture = windowStart;
  const onTimeArrival = windowEnd - required;
  return {
    earliest: Math.min(onTimeDeparture, onTimeArrival),
    latest: Math.max(onTimeDeparture, onTimeArrival),
  };
}

/** Keeps a leg between those two ends. */
export function clampStart(
  start: number,
  windowStart: number,
  windowEnd: number,
  required: number,
): number {
  const { earliest, latest } = travelBounds(windowStart, windowEnd, required);
  return Math.min(Math.max(start, earliest), latest);
}

/**
 * Snaps to the clock, not to wherever the block happened to start.
 *
 * A user dragging a departure thinks in quarter-hours of the day — "I'll leave
 * at half past" — not in quarter-hours measured from an arbitrary default.
 *
 * Applied on the drag path only, never on read. Two positions are computed
 * rather than chosen — the default, and the re-placement after a mode change —
 * and both exist to land an arrival exactly on an event's start time. Snapping
 * those would move the arrival by up to seven minutes to buy a rounder
 * departure, which is the wrong trade in both cases.
 */
export const snapToStep = (m: number) =>
  Math.round(m / DRAG_STEP_MINUTES) * DRAG_STEP_MINUTES;

/**
 * Builds every leg for one day's timed events.
 *
 * Walks the day in order, remembering the last physical location separately
 * from the last event, which is what lets an online session pass the baton
 * along rather than break the chain.
 *
 * `spanOf` decides when you leave and when you have to arrive. Callers on My
 * Schedule pass the *attended* span, because that is what a journey actually
 * runs between: slipping out of a summit at four gives you the half hour that
 * its published six o'clock finish did not, and a warning that survives the
 * user solving the problem is a warning they learn to ignore. The default is
 * published times, for callers with no trims to hand.
 *
 * With a hotel, the day also starts and ends there: a journey from it to the
 * first event you go to in person, arriving as it starts, and one back from
 * the last, leaving as it ends. Neither can be late, and neither moves: their
 * window is the journey itself, so no dashed box claims the morning.
 */
export function buildTravelLegs(
  dayKey: string,
  timed: Opportunity[],
  choices: LegChoices = {},
  spanOf: SpanOf = publishedSpan,
  home: Home | null = null,
): TravelLeg[] {
  const ordered = [...timed]
    .filter((e) => !e.isAllDay && startMinute(e) != null)
    .sort((a, b) => spanOf(a).start - spanOf(b).start);

  const legs: TravelLeg[] = [];
  let lastPhysical: Opportunity | null = null;
  let previous: Opportunity | null = null;

  for (const event of ordered) {
    if (isOnline(event)) {
      // No journey, but it still occupies you: the next departure waits on it.
      previous = event;
      continue;
    }

    if (lastPhysical && previous) {
      const windowStart = spanOf(previous).end;
      const windowEnd = spanOf(event).start;

      // Overlapping events are a clash, not a journey; there is no gap to place
      // a block in and the overlap warning already covers it.
      if (windowEnd > windowStart && windowEnd - windowStart <= MAX_WINDOW_MINUTES) {
        const id = legId(dayKey, lastPhysical, event);
        const choice = choices[id] ?? {};
        const estimate = estimateTravel(lastPhysical, event, choice.mode);

        if (estimate) {
          const required = estimate.minutes;
          const start =
            choice.startMinutes == null
              ? defaultStart(windowStart, windowEnd, required)
              : clampStart(choice.startMinutes, windowStart, windowEnd, required);
          const shortBy = Math.max(0, required - (windowEnd - windowStart));

          legs.push({
            id,
            from: lastPhysical,
            to: event,
            previous,
            windowStart,
            windowEnd,
            estimate,
            startMinutes: start,
            tight: shortBy > 0,
            shortBy,
            viaOnline: previous !== lastPhysical,
          });
        }
      }
    }

    lastPhysical = event;
    previous = event;
  }

  const physical = ordered.filter((e) => !isOnline(e));
  if (home && physical.length) {
    const place = homePlace(home, dayKey);
    const first = physical[0];
    const last = physical[physical.length - 1];

    const outId = legId(dayKey, place, first);
    const outChoice = choices[outId] ?? {};
    const out = estimateTravel(place, first, outChoice.mode);
    if (out) {
      const estimate = { ...out, from: HOME_WORD };
      const windowEnd = spanOf(first).start;
      const windowStart = windowEnd - estimate.minutes;
      legs.unshift({
        id: outId,
        from: place,
        to: first,
        previous: place,
        windowStart,
        windowEnd,
        estimate,
        startMinutes:
          outChoice.startMinutes == null
            ? windowEnd - estimate.minutes
            : clampStart(outChoice.startMinutes, windowStart, windowEnd, estimate.minutes),
        tight: false,
        shortBy: 0,
        viaOnline: false,
      });
    }

    const backId = legId(dayKey, last, place);
    const backChoice = choices[backId] ?? {};
    const back = estimateTravel(last, place, backChoice.mode);
    if (back) {
      const estimate = { ...back, to: HOME_WORD };
      // Free once the day is over, an online session after the last venue included.
      const windowStart = Math.max(...ordered.slice(ordered.indexOf(last)).map((e) => spanOf(e).end));
      const windowEnd = windowStart + estimate.minutes;
      legs.push({
        id: backId,
        from: last,
        to: place,
        previous: last,
        windowStart,
        windowEnd,
        estimate,
        startMinutes:
          backChoice.startMinutes == null
            ? windowStart
            : clampStart(backChoice.startMinutes, windowStart, windowEnd, estimate.minutes),
        tight: false,
        shortBy: 0,
        viaOnline: false,
      });
    }
  }

  return legs;
}

/**
 * The end of a leg's block. Named because it is read in three places and the
 * overhang past `windowEnd` is easy to forget.
 */
export const legEnd = (leg: TravelLeg) => leg.startMinutes + leg.estimate.minutes;

/**
 * Re-places a leg when its mode changes, keeping the arrival time.
 *
 * Chosen over keeping the departure: the fixed point in a journey is almost
 * always when you have to be there. Switching from transit to a bike that takes
 * fifteen minutes less should mean leaving fifteen minutes later, not arriving
 * fifteen minutes early.
 */
export function restartForMode(leg: TravelLeg, minutes: number): number {
  const arrival = legEnd(leg);
  return clampStart(arrival - minutes, leg.windowStart, leg.windowEnd, minutes);
}

export { travelOptions };

/**
 * The journey into one event, as the day view would draw it.
 *
 * The callout used to answer this question with its own routine, and the two
 * drifted apart: back-to-back events at one address got no block on the
 * schedule and a "you would be 10 min late" warning in the panel, because only
 * one of them knew that a zero-length gap is not a journey. There is now a
 * single implementation, and the callout asks it the same question the column
 * does.
 */
export function findLegInto(
  target: Opportunity,
  scheduled: Opportunity[],
  choices: LegChoices = {},
  spanOf: SpanOf = publishedSpan,
  home: Home | null = null,
): TravelLeg | null {
  return dayLegsWith(target, scheduled, choices, spanOf, home).find((l) => l.to.id === target.id) ?? null;
}

/** The journey out of one event to the next one on its day. The same question, asked the other way. */
export function findLegOutOf(
  target: Opportunity,
  scheduled: Opportunity[],
  choices: LegChoices = {},
  spanOf: SpanOf = publishedSpan,
  home: Home | null = null,
): TravelLeg | null {
  return dayLegsWith(target, scheduled, choices, spanOf, home).find((l) => l.from.id === target.id) ?? null;
}

function dayLegsWith(
  target: Opportunity,
  scheduled: Opportunity[],
  choices: LegChoices,
  spanOf: SpanOf,
  home: Home | null,
): TravelLeg[] {
  if (target.isAllDay || startMinute(target) == null) return [];

  const day = localDate(target);
  const sameDay = scheduled.filter((e) => localDate(e) === day);
  // The target need not be on the schedule yet — this is what the callout shows
  // *before* you add it, which is when the warning is most useful.
  if (!sameDay.some((e) => e.id === target.id)) sameDay.push(target);

  return buildTravelLegs(day, sameDay, choices, spanOf, home);
}
